import _defineProperty from "@babel/runtime/helpers/defineProperty";
/* eslint-disable max-params */
import { ValidationManager, stripDashes, parseString, SubType, PaymentMethodsCommandNames, StatusTypes, SpecType, sendRioEvent } from '@chegg/commerce-utils';
import { Component } from '../../../constants/CallbackConstants';
import { ERRORS, InlineErrors } from '../../../constants/Errors';
import { defaultHostedFields } from './DefaultHostedFields';
import { CardTypes } from '../../../constants/PaymentTypes';
import { getString } from '../../../constants/Strings';
import { doFireStatusUpdate, sendRioEventCallback } from '../../../utils/CallbackUtils';
import { sendNewRelicMessage } from '../../../utils/NewRelicUtils';
import { initMOP, updateFormFieldData, updatePaymentState } from '../../../redux/PaymentState/PaymentState.actions';
var _ValidationManager$ty = ValidationManager.types,
  CCFIELDS = _ValidationManager$ty.CCFIELDS,
  VALIDATION_TYPES = _ValidationManager$ty.VALIDATION_TYPES;
/**
 * As user types in the credit card number, braintree returns a subset of credit card
 * types that match the inputted number.  This EventHandler converts that list into a
 * map of matchedCards and saves it to redux paymentState.
 * @param data the hostedFields state object
 * @param dispatch redux Dispatch
 */
export var handleCardTypeChange = function handleCardTypeChange(data, dispatch, rioNamespace) {
  var cards = data.cards;
  var matchedCards = cards.reduce(function (a, c) {
    a[stripDashes(c.type)] = true;
    return a;
  }, {});
  if (cards.length === 1 &&
  // @ts-ignore
  CardTypes[stripDashes(cards[0].type.toUpperCase())]) {
    sendRioEvent({
      info: parseString(getString('APP.RIO.EVENTS.EL_NAME.CARD_TYPE_SELECTED'), {
        cardType: cards[0].niceType
      }),
      specType: SpecType.COMPONENT_VIEW,
      subType: SubType.COMPONENT,
      callback: sendRioEventCallback,
      namespace: rioNamespace,
      force: false
    });
  }
  dispatch(updatePaymentState({
    matchedCards: matchedCards
  }));
};

/**
 * Function that is called whenever a hosted field is either focused, blurred,
 * or Braintree detects that field is empty or notEmpty, or a validityChange event
 * is triggered.
 * @param event The event (state) object of the current hosted field
 * @param focused Specifies whether the active field
 * @param instance the current HostedFields instance
 * @param dispatch Redux dispatch hook
 */
export var updateFormFields = function updateFormFields(event, focused, instance, dispatch, rioNamespace) {
  var field = event.fields[event.emittedBy];
  var fieldName = event.emittedBy;
  var error = '';
  var ariaInvalid = false;

  // if it's required, set the error state
  if (field.isEmpty && !focused) {
    error = ValidationManager.getMessage(VALIDATION_TYPES.REQUIRED, field.container.id);
    ariaInvalid = true;
  }
  // if it's not valid and either potentiallyvalid or potentiallyinvalid, not empty and not focused :)
  else if (!field.isValid && (!field.isPotentiallyValid ||
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  field.isPotentiallyValid && !field.isEmpty && !focused)) {
    var validationType = fieldName === CCFIELDS.CARDHOLDER_NAME ? VALIDATION_TYPES.ONLYLETTERSANDCERTAINCHARS : VALIDATION_TYPES.INVALID;
    error = ValidationManager.getMessage(validationType, field.container.id);
    ariaInvalid = true;
  }
  instance.setAttribute({
    field: event.emittedBy,
    attribute: 'aria-invalid',
    value: ariaInvalid
  });
  if (error.length > 0) {
    sendRioEvent({
      info: parseString(getString('APP.RIO.EVENTS.VALUE.INVALID_FIELD'), {
        fieldName: field.container.id
      }),
      specType: SpecType.NOTIFICATION,
      subType: SubType.ERROR,
      callback: sendRioEventCallback,
      namespace: rioNamespace,
      message_detail: field.isEmpty ? InlineErrors.REQUIRED : InlineErrors.INVALID
    });
  }
  dispatch({
    type: updateFormFieldData.type,
    payload: _defineProperty({}, field.container.id, {
      value: '',
      error: error,
      focused: field.isFocused,
      empty: field.isEmpty
    })
  });
};
/**
 * this Event Handler is triggered when the user clears the value out of a hosted field.
 * Updates the error state of the field that triggered this callback.
 * @param event braintree state object
 * @param dispatch redux Dispatch
 */
export var handleEmptyState = function handleEmptyState(event, dispatch) {
  var field = event.fields[event.emittedBy];
  dispatch({
    type: updateFormFieldData.type,
    payload: _defineProperty({}, field.container.id, {
      value: '',
      error: ValidationManager.getMessage(VALIDATION_TYPES.REQUIRED, field.container.id)
    })
  });
};

/**
 * Clears out and resets all hostedFields to initial state.  Captures a new relic error
 * when no HostedFields instance is passed in.
 * @param instance braintree.HostedFields instance
 * @param dispatch redux Dispatch
 */
export var clearFields = function clearFields(instance, dispatch) {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (instance) {
    var _instance$getState;
    dispatch(initMOP({})); //clear errors and non-hosted field values

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if ((_instance$getState = instance.getState()) !== null && _instance$getState !== void 0 && _instance$getState.fields.hasOwnProperty(CCFIELDS.CARD_NUMBER)) {
      instance.clear(CCFIELDS.CARD_NUMBER);
    }
    instance.clear(CCFIELDS.CVV);
    instance.clear(CCFIELDS.EXPIRATION_DATE);
    instance.clear(CCFIELDS.CARDHOLDER_NAME);
  } else {
    var errMsg = 'no hostedFields instance';
    var err = new Error(errMsg);
    sendNewRelicMessage(errMsg);
    throw err;
  }
};

/**
 * Formats all braintree error messages into readable, user-friendly error messages.
 * This function is triggered when the hostedFields.tokenize call fails.
 * @param errors map of credit card form fields (cardholderName, number, expirationDate, cvv)
 * @param err error that comes back when hostedFields.tokenize fails
 * @param instance the HostedFields instance
 * @param dispatch the redux dispatch
 */
export var normalizeErrorMessage = function normalizeErrorMessage(errors, err, instance, dispatch, rioNamespace) {
  switch (err.code) {
    case 'HOSTED_FIELDS_FIELDS_INVALID':
      // some fields are missing errors. return list of errors. braintree does not mention exact reason why field is invalid
      var errorMsgInvalidField;
      var currentState = instance && instance.getState();
      var _ref = currentState,
        fields = _ref.fields;
      err.details.invalidFieldKeys.forEach(function (field) {
        errorMsgInvalidField = ValidationManager.getMessage(VALIDATION_TYPES.INVALID, field);
        errors[field] = {
          value: '',
          error: errorMsgInvalidField,
          // @ts-ignore
          empty: fields[field].isEmpty
        };
        sendRioEvent({
          info: parseString(getString('APP.RIO.EVENTS.VALUE.INVALID_FIELD'), {
            fieldName: field
          }),
          specType: SpecType.NOTIFICATION,
          subType: SubType.ERROR,
          callback: sendRioEventCallback,
          namespace: rioNamespace,
          // @ts-ignore
          message_detail: fields[field].isEmpty ? InlineErrors.REQUIRED : InlineErrors.INVALID
        });
      });
      break;
    case 'HOSTED_FIELDS_FIELDS_EMPTY':
      Object.keys(defaultHostedFields).forEach(function (fieldName) {
        var fieldErr = ValidationManager.getMessage(VALIDATION_TYPES.REQUIRED, fieldName);
        errors[fieldName] = {
          value: '',
          error: fieldErr,
          empty: true
        };
        sendRioEvent({
          info: parseString(getString('APP.RIO.EVENTS.VALUE.INVALID_FIELD'), {
            fieldName: fieldName
          }),
          specType: SpecType.NOTIFICATION,
          subType: SubType.ERROR,
          callback: sendRioEventCallback,
          namespace: rioNamespace,
          message_detail: InlineErrors.REQUIRED
        });
      });
      break;
    default:
      // tokenization failed (see tokenize error codes on https://braintree.github.io/braintree-web/3.51.0/BraintreeError.html )
      doFireStatusUpdate([{
        status: StatusTypes.ERROR,
        command: PaymentMethodsCommandNames.FETCH_PAYMENT_METHOD,
        payload: {
          component: Component.HOSTED_FIELDS_SERVICE,
          errors: [ERRORS.BRAINTREE_TOKENIZATION_FAILURE]
        }
      }]);
      break;
  }
  return errors;
};


//////////////////
// WEBPACK FOOTER
// ./node_modules/@chegg/writing-checkout/node_modules/@chegg/payment-methods/dist/esm/business/processors/braintree/HostedFieldsServiceHelper.js
// module id = 4588
// module chunks = 11